import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToaster } from "../../common/hooks";
import { ListItem, ListItemText, Typography } from "@mui/material/";
import LoadingButton from "@mui/lab/LoadingButton";
import makeStyles from "@mui/styles/makeStyles";
import styles from "../styles.js";
import { logToSentry } from "../../config/Sentry";

const IndexDBDump = () => {
    const { t, __ } = useTranslation();
    const classes = makeStyles(styles)();
    const toast = useToaster();

    const [loading, setLoading] = useState(false);

    const convertKeysToSnakeCase = (obj) => {
        if (typeof obj !== "object" || obj === null) return obj;

        if (Array.isArray(obj)) {
            return obj.map(convertKeysToSnakeCase);
        }

        return Object.keys(obj).reduce((acc, key) => {
            const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
            acc[snakeKey] = convertKeysToSnakeCase(obj[key]);
            return acc;
        }, {});
    };

    // Helper function to retrieve all data with keys from a store
    const getAllDataWithKeys = (transaction, storeName) => {
        return new Promise((resolve, reject) => {
            const store = transaction.objectStore(storeName);
            const request = store.openCursor();
            const results = [];

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    results.push({ key: cursor.key, value: cursor.value });
                    cursor.continue();
                } else {
                    resolve(results);
                }
            };

            request.onerror = () => reject(request.error);
        });
    };

    const dumpIndexDB = (dbName) => {
        const dbRequest = indexedDB.open(dbName);

        dbRequest.onsuccess = async (event) => {
            const db = event.target.result;
            const transaction = db.transaction(db.objectStoreNames, "readonly");
            const data = {};

            for (const storeName of db.objectStoreNames) {
                data[storeName] = await getAllDataWithKeys(
                    transaction,
                    storeName
                );
            }

            const snakeCaseData = convertKeysToSnakeCase(data);
            const jsonString = JSON.stringify(snakeCaseData, null, 2);
            const blob = new Blob([jsonString], { type: "application/json" });
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `${dbName}_export.json`;
            a.click();
            URL.revokeObjectURL(url);

            console.log("Export with keys completed!");
        };

        dbRequest.onerror = (event) => {
            console.error("Error opening IndexedDB:", event.target.error);
        };
    };

    const downloadData = async () => {
        try {
            setLoading(true);
            await dumpIndexDB("keyval-store");
            toast.success("Data downloaded successfully");
        } catch (error) {
            const errStr = error.toString();
            console.error(error);
            toast.error("Data fixing error!");
            logToSentry("Index DB dump ", {
                error,
                errStr,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <ListItem
            className={classes.listItem}
            key={`data_export_item`}
            secondaryAction={
                <LoadingButton
                    loading={loading}
                    variant="contained"
                    onClick={downloadData}
                    disableElevation
                >
                    {t("download")}
                </LoadingButton>
            }
        >
            <ListItemText
                primary={
                    <Typography color="inherit">{t("dataExport")}</Typography>
                }
            />
        </ListItem>
    );
};

export default IndexDBDump;
